import React from 'react';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { PlatformDraft } from '../../types/api';
import { getPlatformLabel } from '../../constants/platforms';

interface PlatformContentTabsProps {
  platformDrafts: Record<string, PlatformDraft>;
  activePlatform: string;
  onActivePlatformChange: (platform: string) => void;
  onContentChange: (platform: string, content: string) => void;
}

export const PlatformContentTabs: React.FC<PlatformContentTabsProps> = ({
  platformDrafts,
  activePlatform,
  onActivePlatformChange,
  onContentChange,
}) => {
  const platforms = Object.keys(platformDrafts);
  const activeDraft = platformDrafts[activePlatform];

  if (platforms.length === 0) {
    return null;
  }

  return (
    <Box>
      <Tabs
        value={activePlatform}
        onChange={(_, value) => onActivePlatformChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {platforms.map((platform) => {
          const draft = platformDrafts[platform];
          return (
            <Tab
              key={platform}
              value={platform}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getPlatformLabel(platform)}
                  {!draft.within_limit && (
                    <Chip label="Over limit" color="error" size="small" />
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {activeDraft && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {getPlatformLabel(activePlatform)} draft
            </Typography>
            <Typography
              variant="caption"
              color={activeDraft.within_limit ? 'text.secondary' : 'error.main'}
              fontWeight={activeDraft.within_limit ? 400 : 600}
            >
              {activeDraft.char_count} / {activeDraft.char_limit}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={activeDraft.content}
            onChange={(e) => onContentChange(activePlatform, e.target.value)}
            error={!activeDraft.within_limit}
            helperText={
              !activeDraft.within_limit
                ? `Trim ${activeDraft.char_count - activeDraft.char_limit} characters for ${getPlatformLabel(activePlatform)}`
                : undefined
            }
          />
        </>
      )}
    </Box>
  );
};

export default PlatformContentTabs;
